import React, { useState } from 'react';
import { StudentTestimonial } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { PlayIcon, CheckCircleIcon } from './icons';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

interface TestimonialTimelineProps {
  testimonies: StudentTestimonial[];
}

const TestimonialTimeline: React.FC<TestimonialTimelineProps> = ({ testimonies }) => {
  const { colors } = useTheme();
  const [expandedVideo, setExpandedVideo] = useState<string | null>(null);
  
  return (
    <div className="relative">
      {/* Timeline Line */}
      <div 
        className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full rounded-full hidden md:block"
        style={{
          background: `linear-gradient(to bottom, ${colors.primary.main}20, ${colors.accent.mint}40, ${colors.primary.main}20)`
        }}
      />

      {/* Timeline Items */}
      <div className="space-y-16">
        {testimonies.map((testimony, index) => {
          const isEven = index % 2 === 0;
          const animationRef = useScrollAnimation({ 
            animationClass: isEven ? 'animate-slideInLeft' : 'animate-slideInRight' 
          });
          
          return (
            <div 
              key={testimony._id}
              ref={animationRef.ref}
              className={`relative ${animationRef.className}`}
            >
              {/* Timeline Node */}
              <div 
                className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full border-4 hidden md:block z-10"
                style={{
                  backgroundColor: colors.background.default,
                  borderColor: colors.accent.mint,
                  top: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
              >
                <div 
                  className="absolute inset-1 rounded-full animate-ping"
                  style={{ backgroundColor: colors.accent.mint }}
                />
              </div>

              {/* Content */}
              <div className={`grid md:grid-cols-2 gap-8 items-center ${isEven ? '' : 'md:flex-row-reverse'}`}>
                {/* Empty Space for alternating layout */}
                {isEven && <div className="hidden md:block" />}
                
                {/* Testimonial Card */}
                <div 
                  className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                  style={{
                    backgroundColor: colors.background.paper,
                    boxShadow: `0 10px 30px -10px ${colors.accent.mint}30`
                  }}
                >
                  {/* Header */}
                  <div 
                    className="p-6 pb-4"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary.main}10 0%, ${colors.accent.mint}10 100%)`
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-xl font-bold mb-1" style={{ color: colors.text.primary }}>
                          {testimony.studentName}
                        </h4>
                        <div className="flex items-center gap-2">
                          <CheckCircleIcon className="w-5 h-5" style={{ color: colors.accent.mint }} />
                          <span className="text-sm font-medium" style={{ color: colors.accent.mint }}>
                            Course Completed
                          </span>
                        </div>
                      </div>
                      
                      {/* Quote Icon */}
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          background: `linear-gradient(135deg, ${colors.primary.main}20 0%, ${colors.accent.mint}20 100%)`
                        }}
                      >
                        <svg className="w-6 h-6" fill={colors.accent.mint} viewBox="0 0 24 24">
                          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    {testimony.text && (
                      <p 
                        className="leading-relaxed mb-4 italic"
                        style={{ color: colors.text.secondary }}
                      >
                        "{testimony.text}"
                      </p>
                    )}
                    
                    {/* Video Section */}
                    {testimony.videoUrl && (
                      <div className="mt-4">
                        {expandedVideo === testimony._id ? (
                          <div className="relative">
                            {testimony.videoUrl.includes('youtube.com') || testimony.videoUrl.includes('youtu.be') ? (
                              <div className="relative pt-[56.25%]">
                                <iframe
                                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                                  src={(() => {
                                    let videoId = '';
                                    const url = testimony.videoUrl;
                                    
                                    // Handle YouTube Shorts
                                    if (url.includes('youtube.com/shorts/')) {
                                      videoId = url.split('youtube.com/shorts/')[1].split(/[?&]/)[0];
                                    }
                                    // Handle regular YouTube videos
                                    else if (url.includes('youtube.com/watch?v=')) {
                                      videoId = url.split('watch?v=')[1].split(/[?&]/)[0];
                                    }
                                    // Handle youtu.be short links
                                    else if (url.includes('youtu.be/')) {
                                      videoId = url.split('youtu.be/')[1].split(/[?&]/)[0];
                                    }
                                    
                                    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
                                  })()}
                                  title="YouTube video player"
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </div>
                            ) : (
                              <video 
                                controls 
                                className="w-full rounded-lg"
                                autoPlay
                              >
                                <source src={testimony.videoUrl} type="video/mp4" />
                                Your browser does not support the video tag.
                              </video>
                            )}
                            <button
                              onClick={() => setExpandedVideo(null)}
                              className="mt-2 text-sm font-medium transition-colors"
                              style={{ color: colors.accent.mint }}
                            >
                              Hide Video
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setExpandedVideo(testimony._id)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105"
                            style={{
                              backgroundColor: `${colors.accent.mint}20`,
                              color: colors.accent.mint
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = `${colors.accent.mint}30`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = `${colors.accent.mint}20`;
                            }}
                          >
                            <PlayIcon className="w-5 h-5" />
                            <span>Watch Video Testimonial</span>
                          </button>
                        )}
                      </div>
                    )}
                    
                    {/* Success Metric */}
                    <div 
                      className="mt-4 pt-4 border-t"
                      style={{ borderColor: `${colors.border}50` }}
                    >
                      <div className="flex items-center justify-between text-sm">
                        <span style={{ color: colors.text.secondary }}>Journey Impact</span>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <svg 
                              key={star} 
                              className="w-4 h-4" 
                              fill={colors.accent.mint} 
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Empty Space for alternating layout */}
                {!isEven && <div className="hidden md:block" />}
              </div>
            </div>
          );
        })}
      </div>

      {/* End Node */}
      <div className="flex justify-center mt-12">
        <div 
          className="px-6 py-3 rounded-full font-medium text-sm"
          style={{
            background: `linear-gradient(135deg, ${colors.primary.main}20 0%, ${colors.accent.mint}20 100%)`,
            color: colors.accent.mint
          }}
        >
          Join Our Success Stories
        </div>
      </div>
    </div>
  );
};

export default TestimonialTimeline;